import { bunja } from 'bunja';
import { differenceInDays, isAfter, isBefore } from 'date-fns';
import { atom } from 'jotai';
import { atomWithDefault } from 'jotai/utils';

import { breakpointBunja } from '@flipster/react/misc/responsive/index';
import { JotaiStoreScope } from '@flipster/utils/bunja-jotai';
import { dateToEpochString, timestampToDate } from '@flipster/utils/datetime';
import { getFlipsterConfigsV1Key, getLocalStorage, setLocalStorage } from '@flipster/utils/localStorage';
import Num from '@flipster/utils/num';

import { assetMarginBonusBunja } from '../../../../margin-bonus/state';
import { authBunja } from '../../../auth/state';
import { currentTimeBunja } from '../../../current-time/current-time.state';

export const marginBonusExpirationDays = 1;

export const marginBonusExpirationPopupBunja = bunja(() => {
  const store = bunja.use(JotaiStoreScope);
  const { userAtom } = bunja.use(authBunja);
  const { currentTimeAtom } = bunja.use(currentTimeBunja);
  const { booleanBreakpointAtom } = bunja.use(breakpointBunja);
  const { bonusExpirationStatusAtom, bonusExpirationStatusIsLoadingAtom, hasPositionsAtom } =
    bunja.use(assetMarginBonusBunja);

  const storedExpiryBaseAtom = atomWithDefault((get) => {
    const user = get(userAtom);
    if (!user) return null;
    const storageKey = getFlipsterConfigsV1Key(user.userId, 'marginBonusExpirationHideTimestamp');
    const storedValue = getLocalStorage(storageKey);
    return storedValue ? timestampToDate(storedValue.toString()) : null;
  });

  const shouldShowMarginBonusExpirationPopupAtom = atom(
    (get) => {
      const user = get(userAtom);
      const isMobile = get(booleanBreakpointAtom).isMobile;
      const currentTime = get(currentTimeAtom);
      const bonusStatus = get(bonusExpirationStatusAtom);
      const isBonusExpirationStatusLoading = get(bonusExpirationStatusIsLoadingAtom);
      const hasPositions = get(hasPositionsAtom);
      const storedExpiredDate = get(storedExpiryBaseAtom);

      if (!user || isBonusExpirationStatusLoading) return false;

      const isExpired = storedExpiredDate && isAfter(currentTime, storedExpiredDate);
      if (!bonusStatus.expiry || isExpired) return false;

      const canSkip = storedExpiredDate && isBefore(currentTime, storedExpiredDate);
      if (canSkip) return false;

      const isBonusPopupTriggered =
        hasPositions &&
        isBefore(currentTime, bonusStatus.expiry) &&
        differenceInDays(bonusStatus.expiry, currentTime) <= marginBonusExpirationDays;

      return !isMobile && isBonusPopupTriggered && Num(bonusStatus.amountExpiringInUsdt ?? '0').gt('0');
    },
    (get, set, action: 'close' | 'reset') => {
      const user = get(userAtom);
      if (!user) return;

      const storageKey = getFlipsterConfigsV1Key(user.userId, 'marginBonusExpirationHideTimestamp');
      const bonusStatus = get(bonusExpirationStatusAtom);

      if (action === 'close' && bonusStatus.expiry) {
        const expiryTimestamp = dateToEpochString(bonusStatus.expiry);
        setLocalStorage(storageKey, expiryTimestamp);
        set(storedExpiryBaseAtom, bonusStatus.expiry);
        return;
      }

      if (action === 'reset') {
        setLocalStorage(storageKey, null);
        set(storedExpiryBaseAtom, null);
      }
    },
  );

  const shouldResetStorageAtom = atom((get) => {
    const user = get(userAtom);
    if (!user) return false;

    const currentTime = get(currentTimeAtom);
    const bonusStatus = get(bonusExpirationStatusAtom);
    const storedExpiredDate = get(storedExpiryBaseAtom);

    if (!storedExpiredDate) return false;

    const isExpired = isAfter(currentTime, storedExpiredDate);
    return !bonusStatus.expiry || isExpired;
  });

  bunja.effect(() => {
    function resetPopup() {
      const shouldReset = store.get(shouldResetStorageAtom);
      if (!shouldReset) return;
      store.set(shouldShowMarginBonusExpirationPopupAtom, 'reset');
    }
    resetPopup();
    const unsub = store.sub(bonusExpirationStatusAtom, resetPopup);
    return unsub;
  });

  return {
    shouldShowMarginBonusExpirationPopupAtom,
    bonusExpirationStatusIsLoadingAtom,
  };
});

marginBonusExpirationPopupBunja.debugLabel = 'marginBonusExpirationStateBunja';
